const HEADSHOT = "/assets/michelle-headshot.jpg";

export default function WhyIBuiltThis() {
  return (
    <section
      id="why-i-built-this"
      data-testid="why-i-built-this-section"
      className="relative py-28 sm:py-40 overflow-hidden"
    >
      {/* Softer background — emotional exhale */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1733] via-[#0e1f44]/40 to-[#0a1733]" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 h-[520px] w-[700px] bg-amber-500/[0.04] blur-[160px] rounded-full" aria-hidden />
      </div>

      <div className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          {/* LEFT — Portrait */}
          <div className="lg:col-span-5">
            <div className="reveal lg:sticky lg:top-32">
              <div className="relative max-w-[260px] sm:max-w-[280px]">
                {/* Soft glow */}
                <div className="absolute -inset-6 bg-amber-500/10 blur-3xl opacity-50" aria-hidden />
                <div className="relative overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
                  <img
                    src={HEADSHOT}
                    alt="Michelle — founder, Zivae Marketing"
                    data-testid="why-built-headshot"
                    className="w-full h-auto object-cover"
                  />
                  {/* Subtle vignette */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1733]/40 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />
                </div>

                {/* Name plate */}
                <div className="absolute -bottom-3 left-4 bg-[#0a1733] border border-amber-500/30 px-3 py-1.5">
                  <div className="font-display text-base text-white leading-none">
                    Michelle
                  </div>
                  <div className="mt-1 font-sans-body uppercase text-[8px] tracking-[0.32em] text-amber-400">
                    Founder · Zivae Marketing
                  </div>
                </div>
              </div>

              {/* Microcopy pullquote */}
              <div className="reveal mt-14 pl-5 border-l-2 border-amber-500/60">
                <p className="font-display text-lg sm:text-xl text-white italic leading-snug">
                  "When you control your lead flow, you control your future."
                </p>
                <p className="mt-2 font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400">
                  Better systems create independence.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT — Story */}
          <div className="lg:col-span-7">
            <div className="reveal flex items-center gap-3 mb-7">
              <span className="h-[1px] w-10 bg-amber-500/70" />
              <span className="font-sans-body uppercase text-[11px] tracking-[0.32em] text-amber-400">
                The Story Behind the Systems
              </span>
            </div>

            <h2
              data-testid="why-built-headline"
              className="reveal font-display font-light text-white text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.01em] text-balance"
              style={{ transitionDelay: "80ms" }}
            >
              Why I built{" "}
              <span className="italic text-amber-400">this.</span>
            </h2>

            <div className="mt-10 space-y-7 text-slate-200 text-base sm:text-[17px] leading-[1.75] max-w-[640px]">
              <p
                className="reveal"
                style={{ transitionDelay: "140ms" }}
              >
                I started in the insurance industry in 2017.
              </p>

              <p
                className="reveal"
                style={{ transitionDelay: "180ms" }}
              >
                I'll never forget those early days — walking door-to-door
                during one of the coldest Decembers I can remember. Snow on
                the ground. Leads stuffed in a binder. Determined to become
                the success I needed — not just wanted.
              </p>

              <p
                className="reveal"
                style={{ transitionDelay: "220ms" }}
              >
                I worked with different IMOs and FMOs over the years, and
                through both failure and observation, I learned something
                important:
              </p>

              <p
                className="reveal font-display text-2xl sm:text-3xl text-amber-400 italic leading-snug pl-5 border-l border-amber-500/40 py-2"
                style={{ transitionDelay: "260ms" }}
              >
                The advisors who controlled their lead flow controlled their
                future.
              </p>

              <p
                className="reveal"
                style={{ transitionDelay: "300ms" }}
              >
                At first, I built these systems for myself. I needed a lead
                generation system independent of where I was contracted.
                Because when you control your lead flow, you control your
                income.
              </p>

              <p
                className="reveal"
                style={{ transitionDelay: "340ms" }}
              >
                And honestly? It wasn't easy. I was burned out. Cold calling
                was the worst part. Imagine calling people who literally
                requested help — only to be treated like a random
                telemarketer and hung up on all day.
              </p>

              <p
                className="reveal text-slate-300"
                style={{ transitionDelay: "380ms" }}
              >
                That kind of pressure changes you.
              </p>

              <p
                className="reveal"
                style={{ transitionDelay: "420ms" }}
              >
                So I started building systems. Not because it sounded
                impressive. Because I was tired.
              </p>

              <ul
                className="reveal space-y-2 text-slate-300 pl-1"
                style={{ transitionDelay: "460ms" }}
              >
                <li className="flex items-baseline gap-3">
                  <span className="text-amber-500/80 text-xs">—</span>
                  <span>Tired of chasing.</span>
                </li>
                <li className="flex items-baseline gap-3">
                  <span className="text-amber-500/80 text-xs">—</span>
                  <span>Tired of inconsistency.</span>
                </li>
                <li className="flex items-baseline gap-3">
                  <span className="text-amber-500/80 text-xs">—</span>
                  <span>Tired of survival mode.</span>
                </li>
              </ul>

              <p
                className="reveal"
                style={{ transitionDelay: "560ms" }}
              >
                Everything I've built came from the pain I experienced
                personally as an advisor trying to create stability and
                independence.
              </p>

              <p
                className="reveal"
                style={{ transitionDelay: "600ms" }}
              >
                Today, I wake up to booked appointments because I invested
                in myself. In my experience. My failures. My lessons. My
                growth.
              </p>

              <p
                className="reveal font-display text-2xl sm:text-3xl text-white leading-snug"
                style={{ transitionDelay: "640ms" }}
              >
                And now, I help other advisors do the same.
              </p>

              <p
                className="reveal text-slate-300 italic"
                style={{ transitionDelay: "680ms" }}
              >
                Because independence changes everything.
              </p>
            </div>

            {/* Signature line */}
            <div
              className="reveal mt-12 flex items-center gap-4 pt-8 border-t border-white/10"
              style={{ transitionDelay: "720ms" }}
            >
              <span className="font-display text-3xl text-amber-400 italic leading-none">
                Michelle
              </span>
              <span className="h-[1px] w-12 bg-amber-500/40" />
              <span className="font-sans-body uppercase text-[10px] tracking-[0.32em] text-slate-400">
                Built it. Lived it. Teaching it.
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
